import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { CREATORS, MODELS } from "@/data/characters";
import { windowState } from "@/lib/windows";
import { RecruitmentTimeline } from "@/components/recruitment/RecruitmentTimeline";

const PATHS = [
  { to: "/apply/student", kind: "student", label: "Student / Emerging Creative", blurb: "Join a cohort, get a real brief, leave with a real portfolio.", img: CREATORS[0].img, tag: "full application" },
  { to: "/apply/talent", kind: "talent", label: "Model / Creative Talent", blurb: "Get in front of the lens as editorial creative talent.", img: MODELS[1].img, tag: "interest form" },
  { to: "/apply/school", kind: "school", label: "School Partner", blurb: "Bring project-based creative experience to your students.", img: CREATORS[5].img, tag: "partnership inquiry" },
  { to: "/apply/brand", kind: "brand", label: "Brand / Sponsor", blurb: "Bring a brief, sponsor an Issue, provide tools, space, or opportunity.", img: CREATORS[4].img, tag: "partnership inquiry" },
  { to: "/apply/mentor", kind: "mentor", label: "Mentor / Industry Professional", blurb: "Guide the next generation of creatives on real campaigns.", img: CREATORS[6].img, tag: "interest form" },
];

const StatusChip = ({ kind }) => {
  const w = windowState(kind);
  const cls = w.status === "open"
    ? "border-[#3ad07a]/60 bg-[#3ad07a]/10 text-[#3ad07a]"
    : w.status === "sealed"
    ? "border-gold/60 bg-gold/10 text-goldlight"
    : "border-silver/40 text-silver/60";
  const text = w.status === "open"
    ? (kind === "school" ? "OPEN" : `OPEN · CLOSES ${w.closesLabel.toUpperCase()}`)
    : w.status === "sealed"
    ? `SEALED · OPENS ${w.opensShort}`
    : "WINDOW CLOSED";
  return <span data-testid={`path-status-${kind}`} className={`border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] ${cls}`}>{text}</span>;
};

export default function Apply() {
  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="Apply"
        titleLines={["How do you want", "to enter", "the Village?"]}
        subtitle="Pick your path. Every submission is saved, confirmed by email, and reviewed by hand — never by a robot."
      />

      <section className="grain-dark relative bg-onyx py-16 sm:py-24">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
          <div className="grid gap-6 md:grid-cols-2" data-testid="apply-paths">
            {PATHS.map((p, i) => (
              <Reveal key={p.to} delay={i * 0.07} className={i === 0 ? "md:col-span-2" : ""}>
                <Link
                  to={p.to}
                  data-testid={`apply-path-${p.to.split("/").pop()}`}
                  className="group flex h-full items-stretch overflow-hidden gold-frame bg-charcoal transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(201,162,39,0.2)]"
                >
                  <div className={`panel-mono relative w-28 shrink-0 overflow-hidden border-r border-gold/30 sm:w-40 ${i === 0 ? "sm:w-56" : ""}`}>
                    <img src={p.img} alt="" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-onyx/60 to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col justify-between p-5 sm:p-7">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-block border border-gold/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold">
                          {p.tag}
                        </span>
                        <StatusChip kind={p.kind} />
                      </div>
                      <h2 className="mt-3 font-display text-2xl uppercase leading-[0.95] tracking-tight text-cream sm:text-4xl">{p.label}</h2>
                      <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-silver/80 sm:text-base">{p.blurb}</p>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-2 font-head text-sm font-extrabold uppercase tracking-[0.15em] text-goldlight">
                      {windowState(p.kind).status === "open" ? (p.kind === "brand" || p.kind === "mentor" ? "Request clearance" : p.kind === "school" ? "Enter here" : "Submit your dossier") : "Review the file"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <div className="mt-10 border border-gold/25 bg-charcoal/70 p-6 sm:p-8">
              <Kicker>Good to know</Kicker>
              <p className="mt-4 font-body text-sm leading-relaxed text-silver/75">
                Applying is not acceptance, and participation is not guaranteed employment. Compensation varies by
                project and is always disclosed before you participate. School credit and hours require your
                school's approval. We review everything by hand.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <RecruitmentTimeline />
    </div>
  );
}
